import type {
  SimulationRequestInterface,
  SimulationResponseInterface,
} from 'store/simulation/types';

export const SIMULATE_MIN_MAX = 'SIMULATE_MIN_MAX';

/**
 * Interface for the PG slice of the state
 */

export const DefaultWorkIndexValueVariances = {
  CONE_CRUSHER: 2,
};

export const DefaultSpecificGravityPercentVariances = {
  CONE_CRUSHER: 6,
};

export enum PGTypes {
  CONE_CRUSHER = 'cone',
  JAW_CRUSHER = 'jaw',
  JAW_CRUSHER_WITH_FEEDING_EQUIPMENT = 'jawWithFeeding',
  VSI_CRUSHER = 'vsi',
  SCREENS = 'screens',
  MINING_APPLICATION_TYPE_ONE = 'miningApplication1',
  MINING_APPLICATION_TYPE_TWO = 'miningApplication2',
}

export interface PGTopCardSingleItemInfo {
  uiLabel: string;
  minimumPerformanceCriteria: string[];
}

export interface PGTopCardInfoInterface {
  [key: string]: PGTopCardSingleItemInfo;
}
export interface PGInterface {
  selectedPG: string;
  workIndexVariance: number;
  specificGravityPercentVariance: number;
  feedSizePercentVariance: number;
  minSimulationResponse: SimulationResponseInterface | null;
  maxSimulationResponse: SimulationResponseInterface | null;
  rounding: boolean;
  PGRequest: PGRequestInterface;
}

export interface PGBaseInterface {
  certifiedIssuer: string;
  projectNo: string;
  flowsheetNo: string;
  lastSimulation: string;
  materialName: string;
  screenshotContent: string;
}

export interface PGConeCrusherRequestInterface extends PGBaseInterface {
  goods: string;
  specificGravity: number;
  f100: number;
  f90: number;
  f50: number;
  f15: number;
  workIndex: number;
  minimumCapacity: number;
  europeanNorm: string;
  p80: number;
}

export interface PGJawCrusherRequestInterface extends PGBaseInterface {
  opportunityNo: string;
  goods: string;
  layoutDrawingNo: string;
  layoutDrawingDate: string;
  specificGravity: string;
  f100: string;
  f80: string;
  css: string;
  cssPercent: string;
  cssHalf: string;
  cssHalfPercent: string;
  workIndex: string;
  minimumCapacity: string;
  europeanNorm: string;
  pValue: string;
  pPercent: string;
}
export interface PGJawCrusherWithFeedingEquipmentRequestInterface extends PGBaseInterface {
  opportunityNo: string;
  goods: string;
  layoutDrawingNo: string;
  layoutDrawingDate: string;
  specificGravity: string;
  f100: string;
  f80: string;
  css: string;
  cssPercent: string;
  cssHalf: string;
  cssHalfPercent: string;
  separationTopDeck: string;
  separationTopDeckPercent: string;
  separationSecondDeck: string;
  separationSecondDeckPercent: string;
  workIndex: string;
  minimumCapacity: string;
  europeanNorm: string;
  pValue: string;
  pPercent: string;
}
export interface PGVSICrusherRequestInterface extends PGBaseInterface {
  opportunityNo: string;
  goods: string;
  layoutDrawingNo: string;
  layoutDrawingDate: string;
  specificGravity: string;
  f100: string;
  f85: string;
  f50: string;
  separation4mmPercent: string;
  separation2mmPercent: string;
  workIndex: string;
  feedMaterial: string;
  sif1: string;
  sif2: string;
  modelName: string;
  minimumCapacity: string;
  maximumCapacity: string;
}

export interface PGMiningApplicationTypeOneRequestInterface extends PGBaseInterface {
  opportunityNo: string;
  layoutDrawingNo: string;
  layoutDrawingDate: string;
  specificGravity: string;
  f100: string;
  f80: string;
  css: string;
  cssPercent: string;
  cssHalf: string;
  cssHalfPercent: string;
  separationFinalScreen: string;
  separationFinalScreenPercent: string;
  workIndex: string;
  minimumCapacity: string;
  europeanNorm: string;
  pValue: string;
  pPercent: string;
}
export interface PGMiningApplicationTypeTwoRequestInterface extends PGBaseInterface {
  opportunityNo: string;
  layoutDrawingNo: string;
  layoutDrawingDate: string;
  specificGravity: string;
  f100: string;
  f80: string;
  separationTopDeck: string;
  separationTopDeckPercent: string;
  separationFinalScreen: string;
  separationFinalScreenPercent: string;
  separationFinalScreenHalf: string;
  separationFinalScreenHalfPercent: string;
  workIndex: string;
  minimumCapacity: string;
  europeanNorm: string;
  pValue: string;
  pPercent: string;
}

export interface PGScreensRequestInterface extends PGBaseInterface {
  opportunityNo: string;
  goods: string;
  layoutDrawingNo: string;
  layoutDrawingDate: string;
  specificGravity: string;
  separationTopDeck: string;
  separationTopDeckOneAndAHalf: string;
  sepTopDeckOAHPercent: string;
  separationFirstDeck: string;
  sepFirstDeckPercent: string;
  separationSecondDeck: string;
  sepSecondDeckPercent: string;
  separationThirdDeck: string;
  sepThirdDeckPercent: string;
  separationFourthDeck: string;
  sepFourthDeckPercent: string;
  separationLowerDeckHalf: string;
  sepLowerDeckHalfPercent: string;
  minimumCapacity: string;
  maximumCapacity: string;
}

export interface PGRequestInterface {
  template: string;
  [PGTypes.CONE_CRUSHER]: PGConeCrusherRequestInterface;
  [PGTypes.JAW_CRUSHER]: PGJawCrusherRequestInterface;
  [PGTypes.JAW_CRUSHER_WITH_FEEDING_EQUIPMENT]: PGJawCrusherWithFeedingEquipmentRequestInterface;
  [PGTypes.VSI_CRUSHER]: PGVSICrusherRequestInterface;
  [PGTypes.MINING_APPLICATION_TYPE_ONE]: PGMiningApplicationTypeOneRequestInterface;
  [PGTypes.MINING_APPLICATION_TYPE_TWO]: PGMiningApplicationTypeTwoRequestInterface;
  [PGTypes.SCREENS]: PGScreensRequestInterface;
}

export interface PGVarianceData {
  workIndexNumberVariance: number;
  specificGravityPercentVariance: number;
  feedSizeDistributionPercentVariance: number;
}

export interface PGSimulationRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof SIMULATE_MIN_MAX;
  simulateData: SimulationRequestInterface;
  pgVarianceData: PGVarianceData;
}
